import {
  BadgeEuro,
  ClipboardCheck,
  CreditCard,
  FileText,
  Flame,
  Info,
  MessageCircle,
  MousePointerClick,
  PackageCheck,
  Repeat,
  Trash2,
  TriangleAlert,
  Trophy,
  User,
  Wrench,
} from "lucide-react";
import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { AgentChip } from "../AgentChip";
import { formatDate } from "../gulaConstants";
import { timeOf, type TeamMessage } from "./teamChat";

const EVENT_STYLE: Record<string, { icon: typeof Info; className: string }> = {
  sale: { icon: BadgeEuro, className: "text-emerald-600" },
  maintenance: { icon: Repeat, className: "text-emerald-600" },
  received: { icon: PackageCheck, className: "text-sky-600" },
  installed: { icon: Wrench, className: "text-sky-600" },
  requirements: { icon: ClipboardCheck, className: "text-emerald-600" },
  lead_hot: { icon: Flame, className: "text-red-600" },
  address_alert: { icon: TriangleAlert, className: "text-destructive" },
  payment_failed: { icon: CreditCard, className: "text-destructive" },
  goal: { icon: Trophy, className: "text-lime-600" },
  lead_whatsapp: { icon: MessageCircle, className: "text-emerald-600" },
  lead_social: { icon: MessageCircle, className: "text-pink-600" },
  email_click: { icon: MousePointerClick, className: "text-lime-600" },
  info: { icon: Info, className: "text-muted-foreground" },
};

const ClientLink = ({ message }: { message: TeamMessage }) =>
  message.client_id && message.contact_name ? (
    <Link
      to={`/clientes?cliente=${message.client_id}`}
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <User className="h-3 w-3" />
      {message.contact_name}
    </Link>
  ) : null;

const isImage = (type?: string, src?: string) =>
  type?.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(src ?? "");

/** One line of the team chat: a CRM news item, a message or a task. */
export const TeamMessageItem = ({
  message,
  meId,
  isNew,
  onChanged,
}: {
  message: TeamMessage;
  meId?: Identifier;
  isNew: boolean;
  onChanged: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (message.kind === "event") {
    const style = EVENT_STYLE[message.event ?? "info"] ?? EVENT_STYLE.info;
    const Icon = style.icon;
    return (
      <div
        className={cn(
          "flex items-start gap-2 rounded-md px-2 py-1.5 text-sm",
          isNew && "bg-sky-50 dark:bg-sky-950/30",
        )}
      >
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", style.className)} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-muted-foreground">
            <span className="text-foreground">{message.body}</span>{" "}
            <span className="text-xs">· {timeOf(message.created_at)}</span>
          </span>
          {message.client_id ? (
            <div>
              <ClientLink message={message} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const isMine = meId != null && String(message.sales_id) === String(meId);
  const isTask = message.kind === "task";
  const done = !!message.done_at;

  const toggleDone = async () => {
    try {
      await dataProvider.update("gula_team_messages", {
        id: message.id,
        data: { done_at: done ? null : new Date().toISOString() },
        previousData: { id: message.id },
      });
      onChanged();
    } catch {
      notify("No se ha podido guardar", { type: "error" });
    }
  };

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await dataProvider.delete("gula_team_messages", {
        id: message.id,
        previousData: { id: message.id },
      });
      onChanged();
    } catch {
      notify("No se ha podido borrar", { type: "error" });
    }
  };

  return (
    <div
      className={cn(
        "group flex gap-2 rounded-md px-2 py-1.5",
        isNew && "bg-sky-50 dark:bg-sky-950/30",
      )}
      onMouseLeave={() => setConfirmDelete(false)}
    >
      {message.sales_id != null ? (
        <AgentChip
          id={message.sales_id}
          name={message.author_name ?? "?"}
          compact
          className="mt-0.5 shrink-0"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium">
            {message.author_name?.split(" ")[0] ?? "Alguien"}
          </span>
          <span className="text-xs text-muted-foreground">
            {timeOf(message.created_at)}
          </span>
          {isMine ? (
            <button
              type="button"
              onClick={remove}
              className={cn(
                "ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus:opacity-100",
                confirmDelete && "text-destructive opacity-100",
              )}
              aria-label="Borrar mensaje"
            >
              <Trash2 className="h-3 w-3" />
              {confirmDelete ? "¿Borrar?" : null}
            </button>
          ) : null}
        </div>

        {isTask ? (
          <label
            className={cn(
              "flex cursor-pointer items-start gap-2 rounded-md border p-2",
              done
                ? "bg-muted/40"
                : "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20",
            )}
          >
            <Checkbox
              checked={done}
              onCheckedChange={toggleDone}
              className="mt-0.5"
              aria-label="Tarea hecha"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <span
                className={cn(
                  "whitespace-pre-wrap break-words text-sm",
                  done && "text-muted-foreground line-through",
                )}
              >
                {message.body}
              </span>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {message.assignee_id != null ? (
                  <span>
                    Para{" "}
                    <AgentChip
                      id={message.assignee_id}
                      name={message.assignee_name?.split(" ")[0] ?? "alguien"}
                    />
                  </span>
                ) : null}
                {message.due_date ? (
                  <span>· para el {formatDate(message.due_date)}</span>
                ) : null}
                {done ? (
                  <span>
                    · hecho
                    {message.done_by_name
                      ? ` por ${message.done_by_name.split(" ")[0]}`
                      : ""}
                  </span>
                ) : null}
              </span>
            </div>
          </label>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm">
            {message.body}
          </p>
        )}

        {message.attachments?.length ? (
          <div className="flex flex-wrap gap-2">
            {message.attachments.map((file) =>
              isImage(file.type, file.src) ? (
                <a
                  key={file.src}
                  href={file.src}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={file.src}
                    alt={file.title}
                    className="h-20 w-20 rounded-md border object-cover"
                  />
                </a>
              ) : (
                <a
                  key={file.src}
                  href={file.src}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{file.title}</span>
                </a>
              ),
            )}
          </div>
        ) : null}

        {message.client_id ? (
          <div>
            <ClientLink message={message} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
